import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Download, Eye } from 'lucide-react';

const STATUS_COLORS = {
  new: 'bg-[#3B82F6]/20 text-[#60A5FA]',
  contacted: 'bg-[#F59E0B]/20 text-[#F59E0B]',
  interview: 'bg-[#8B5CF6]/20 text-[#A78BFA]',
  hired: 'bg-[#10B981]/20 text-[#10B981]',
  rejected: 'bg-[#EF4444]/20 text-[#EF4444]',
};

const STATUS_LABELS = {
  new: 'Neu', contacted: 'Kontaktiert', interview: 'Interview', hired: 'Eingestellt', rejected: 'Abgelehnt'
};

export default function AdminLeadsPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [detail, setDetail] = useState(null);

  const loadLeads = () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    adminApi.get('/admin/leads', { params })
      .then(r => { setLeads(r.data.leads); setTotal(r.data.total); })
      .catch(() => navigate('/admin/login'));
  };

  useEffect(() => { loadLeads(); }, [statusFilter]);

  const updateStatus = async (leadId, status) => {
    try {
      await adminApi.put(`/admin/leads/${leadId}/status`, { status });
      toast.success(`Status: ${STATUS_LABELS[status]}`);
      loadLeads();
      if (detail?.id === leadId) setDetail(d => ({ ...d, status }));
    } catch { toast.error('Fehler'); }
  };

  const exportCsv = () => {
    const token = localStorage.getItem('admin_token');
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/export?authorization=Bearer ${token}`, '_blank');
  };

  return (
    <AdminLayout>
      <div data-testid="admin-leads-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-outfit font-bold text-2xl text-white">Bewerbungen <span className="text-[#9CA3AF] text-lg font-normal">({total})</span></h1>
          <Button variant="outline" onClick={exportCsv} className="border-white/10 text-[#9CA3AF] hover:bg-white/5" data-testid="export-leads-btn">
            <Download className="w-4 h-4 mr-2" /> CSV Export
          </Button>
        </div>

        <div className="mb-4">
          <Select value={statusFilter} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white" data-testid="filter-lead-status">
              <SelectValue placeholder="Alle Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#121214] border-white/10">
              <SelectItem value="all">Alle Status</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-[#121214] border border-white/5 rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-[#9CA3AF]">Name</TableHead>
                <TableHead className="text-[#9CA3AF]">E-Mail</TableHead>
                <TableHead className="text-[#9CA3AF]">Typ</TableHead>
                <TableHead className="text-[#9CA3AF]">Score</TableHead>
                <TableHead className="text-[#9CA3AF]">Status</TableHead>
                <TableHead className="text-[#9CA3AF] text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.map(lead => (
                <TableRow key={lead.id} className="border-white/5">
                  <TableCell className="text-white text-sm">{lead.firstname} {lead.lastname}</TableCell>
                  <TableCell className="text-[#9CA3AF] text-sm">{lead.email}</TableCell>
                  <TableCell>
                    <Badge className={`${lead.applicantType === 'azubi' ? 'bg-[#3B82F6]/20 text-[#60A5FA]' : 'bg-[#8B5CF6]/20 text-[#A78BFA]'} border-0 text-xs`}>
                      {lead.applicantType === 'azubi' ? 'Azubi' : 'Profi'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-white text-sm font-mono">
                    {lead.runData?.correctCount}/{lead.runData?.totalQuestions} ({lead.runData?.percentCorrect}%)
                  </TableCell>
                  <TableCell>
                    <Select value={lead.status} onValueChange={v => updateStatus(lead.id, v)}>
                      <SelectTrigger className="w-32 h-7 bg-transparent border-0 p-0">
                        <Badge className={`${STATUS_COLORS[lead.status]} border-0 text-xs`}>{STATUS_LABELS[lead.status]}</Badge>
                      </SelectTrigger>
                      <SelectContent className="bg-[#121214] border-white/10">
                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setDetail(lead)} className="text-[#9CA3AF] hover:text-white">
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
          <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md" data-testid="lead-detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit">Bewerbung Details</DialogTitle>
            </DialogHeader>
            {detail && (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-[#9CA3AF]">Name:</span> <span className="text-white">{detail.firstname} {detail.lastname}</span></div>
                  <div><span className="text-[#9CA3AF]">Typ:</span> <span className="text-white">{detail.applicantType === 'azubi' ? 'Azubi' : 'Profi'}</span></div>
                  <div><span className="text-[#9CA3AF]">E-Mail:</span> <span className="text-white">{detail.email}</span></div>
                  <div><span className="text-[#9CA3AF]">Telefon:</span> <span className="text-white">{detail.phone}</span></div>
                  <div><span className="text-[#9CA3AF]">PLZ:</span> <span className="text-white">{detail.postalCode}</span></div>
                  <div><span className="text-[#9CA3AF]">Interessen:</span> <span className="text-white">{detail.interests?.join(', ')}</span></div>
                </div>
                <div className="bg-white/5 rounded-lg p-3 mt-3">
                  <p className="text-[#9CA3AF] text-xs mb-2">Quiz-Ergebnis</p>
                  <p className="text-white">Level: <span className="font-mono">{detail.runData?.level}</span></p>
                  <p className="text-white">Score: <span className="font-mono">{detail.runData?.correctCount}/{detail.runData?.totalQuestions} ({detail.runData?.percentCorrect}%)</span></p>
                  {detail.runData?.rankingScore && <p className="text-white">Ranking: <span className="font-mono">{detail.runData?.rankingScore}</span></p>}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
